import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import toast from "react-hot-toast";
import {Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Lock, Loader2, X, Info, } from 'lucide-react';
import { lumi } from "@/lib/lumi";
import {
  CONTRACT_LABELS,
  ContractType,
  JobCategory,
  JOB_ACTIVITIES,
} from "@/data/jobs";
import {
  NewRecruiterJob,
  RecruiterJob,
  useRecruiterJobs,
} from "@/hooks/useRecruiterJobs";

// Limite stricte : 10 annonces par lot d'import
const MAX_BULK_JOBS = 10;

// En-têtes de la feuille modèle (ordre exact à respecter)
const TEMPLATE_HEADERS = [
  "Titre*",
  "Employeur*",
  "Ville*",
  "Departement",
  "Categorie*",
  "TypeContrat*",
  "Salaire",
  "TarifHoraire",
  "Description",
  "ProfilRecherche",
  "Avantages",
  "Deadline",
  "MissionDate",
  "MissionStartTime",
  "MissionEndTime",
  "MissionHours",
  "MissionPrice",
  "Activites",
  "Urgent",
  "LogementPossible",
  "HorairesFixes",
  "CoursParticuliers",
  "PossibiliteCDI",
] as const;

type TemplateRow = Partial<Record<(typeof TEMPLATE_HEADERS)[number], string | number>>;

// Catégories & types de contrat valides (alignés avec data/jobs.ts)
const VALID_CATEGORIES: JobCategory[] = ["BNSSA", "MNS"];
const VALID_CONTRACTS: ContractType[] = Object.keys(CONTRACT_LABELS) as ContractType[];

interface ParsedJob {
  rowIndex: number;
  data: NewRecruiterJob;
  errors: string[];
}

interface BulkJobImportProps {
  recruiterId: string;
  recruiterEmail?: string;
  recruiterName?: string;
  onImported?: () => void;
}

/**
 * Composant d'import en masse d'annonces via fichier Excel (max 10 par lot).
 *
 * Verrou métier : un nouvel upload n'est possible que lorsque toutes les
 * annonces du dernier lot précédent ont été validées par l'admin
 * (validationStatus === "approved").
 */
export default function BulkJobImport({
  recruiterId,
  recruiterEmail,
  recruiterName,
  onImported,
}: BulkJobImportProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { createJob } = useRecruiterJobs();

  // État du verrou
  const [lockLoading, setLockLoading] = useState(true);
  const [lockedBatch, setLockedBatch] = useState<{
    batchId: string;
    total: number;
    approved: number;
    pending: number;
    rejected: number;
  } | null>(null);

  // Aperçu des lignes parsées
  const [parsed, setParsed] = useState<ParsedJob[] | null>(null);
  const [importing, setImporting] = useState(false);

  // ─── Vérifier l'état du dernier lot d'import ───────────────────────────────
  const fetchLockState = useCallback(async () => {
    if (!recruiterId) return;
    setLockLoading(true);
    try {
      const { list } = await lumi.entities.recruiter_jobs.list({
        filter: { creator: recruiterId },
        sort: { createdAt: -1 },
      });
      const all = (list as RecruiterJob[]) || [];
      // Identifier le dernier batch (le plus récent)
      const batched = all.filter((j) => typeof j.bulkBatchId === "string" && j.bulkBatchId);
      if (batched.length === 0) {
        setLockedBatch(null);
        return;
      }
      // batched[0] est le plus récent (sort desc createdAt)
      const lastBatchId = batched[0].bulkBatchId as string;
      const batchJobs = batched.filter((j) => j.bulkBatchId === lastBatchId);
      const approved = batchJobs.filter((j) => j.validationStatus === "approved").length;
      const rejected = batchJobs.filter((j) => j.validationStatus === "rejected").length;
      const pending = batchJobs.length - approved - rejected;
      // Verrou si au moins une annonce du dernier lot n'est PAS approved
      if (approved < batchJobs.length) {
        setLockedBatch({
          batchId: lastBatchId,
          total: batchJobs.length,
          approved,
          pending,
          rejected,
        });
      } else {
        setLockedBatch(null);
      }
    } catch (err) {
      console.error("[BulkJobImport] fetchLockState failed:", err);
      // En cas d'erreur, on ne bloque pas par défaut (UX dégradée mais utilisable)
      setLockedBatch(null);
    } finally {
      setLockLoading(false);
    }
  }, [recruiterId]);

  useEffect(() => {
    fetchLockState();
  }, [fetchLockState]);

  // ─── Génération du modèle Excel ────────────────────────────────────────────
  // Stratégie : SheetJS écrit le .xlsx en buffer, puis on patche le XML de la
  // feuille « Annonces » avec JSZip pour injecter de vraies <dataValidations>
  // (listes déroulantes natives Excel) — fonctionnalité non supportée à
  // l'écriture par xlsx 0.18.5.
  const handleDownloadTemplate = useCallback(async () => {
    const wb = XLSX.utils.book_new();

    // ─── Feuille 1 : Annonces — en-têtes + 2 lignes d'exemple + lignes vides ──
    const exampleAE: TemplateRow = {
      "Titre*": "BNSSA — Mission ponctuelle surveillance",
      "Employeur*": "Aqua Coaching Exemple",
      "Ville*": "Biarritz",
      Departement: "Pyrénées-Atlantiques (64)",
      "Categorie*": "BNSSA",
      "TypeContrat*": "auto-entrepreneur",
      Salaire: "",
      TarifHoraire: "18 € - 25 €/heure",
      Description: "Surveillance d'un événement aquatique privé sur une journée.",
      ProfilRecherche: "",
      Avantages: "",
      Deadline: "",
      MissionDate: "2025-07-15",
      MissionStartTime: "09:00",
      MissionEndTime: "17:00",
      MissionHours: 8,
      MissionPrice: "",
      Activites: "Surveillance",
      Urgent: "non",
      LogementPossible: "non",
      HorairesFixes: "oui",
      CoursParticuliers: "non",
      PossibiliteCDI: "non",
    };
    const exampleCDI: TemplateRow = {
      "Titre*": "Maître Nageur Sauveteur — CDI",
      "Employeur*": "Centre Aquatique Exemple",
      "Ville*": "Lyon",
      Departement: "Rhône (69)",
      "Categorie*": "MNS",
      "TypeContrat*": "CDI",
      Salaire: "2 200 € - 2 800 € brut/mois",
      TarifHoraire: "",
      Description: "Surveillance et encadrement des cours de natation.",
      ProfilRecherche: "BPJEPS AAN ou BEESAN\nCarte pro valide",
      Avantages: "Mutuelle\n13e mois",
      Deadline: "2025-12-31",
      MissionDate: "",
      MissionStartTime: "",
      MissionEndTime: "",
      MissionHours: "",
      MissionPrice: "",
      Activites: "",
      Urgent: "non",
      LogementPossible: "non",
      HorairesFixes: "oui",
      CoursParticuliers: "non",
      PossibiliteCDI: "non",
    };
    const rows: TemplateRow[] = [exampleAE, exampleCDI];
    for (let i = 0; i < MAX_BULK_JOBS - rows.length; i++) {
      const empty: TemplateRow = {};
      TEMPLATE_HEADERS.forEach((h) => (empty[h] = ""));
      rows.push(empty);
    }
    // Construction explicite via aoa_to_sheet pour garantir la ligne d'en-têtes
    // (json_to_sheet pose des problèmes de dimension/range avec des clés contenant '*').
    const headerRow: string[] = [...TEMPLATE_HEADERS];
    const dataRows: (string | number)[][] = rows.map((r) =>
      TEMPLATE_HEADERS.map((h) => {
        const v = r[h];
        return v === undefined || v === null ? "" : (v as string | number);
      })
    );
    const aoa: (string | number)[][] = [headerRow, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Forcer le range pour éviter toute ambiguïté côté Excel
    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: MAX_BULK_JOBS, c: TEMPLATE_HEADERS.length - 1 },
    });

    // Largeurs de colonnes
    const colWidths: Record<string, number> = {
      "Titre*": 32, "Employeur*": 26, "Ville*": 16, Departement: 22,
      "Categorie*": 12, "TypeContrat*": 22, Salaire: 22, TarifHoraire: 20,
      Description: 40, ProfilRecherche: 30, Avantages: 26, Deadline: 14,
      MissionDate: 14, MissionStartTime: 16, MissionEndTime: 16,
      MissionHours: 14, MissionPrice: 14, Activites: 26, Urgent: 10,
      LogementPossible: 16, HorairesFixes: 14, CoursParticuliers: 18,
      PossibiliteCDI: 16,
    };
    ws["!cols"] = TEMPLATE_HEADERS.map((h) => ({ wch: colWidths[h] || 16 }));

    // NOTE : pas de commentaires de cellule (cell.c) — ils génèrent
    // <legacyDrawing>/<commentList> qui entrent en conflit avec l'ordre OOXML
    // strict imposé pour <dataValidations>, et provoquent l'erreur Excel
    // « Partie remplacée: /xl/worksheets/sheet1.xml ... ». Les infos d'aide
    // sont fournies via la feuille « Aide » + l'encart UI + le prompt natif
    // attaché à chaque liste déroulante.
    XLSX.utils.book_append_sheet(wb, ws, "Annonces");

    // ─── Feuille 2 : Aide ───────────────────────────────────────────────────
    const helpAOA: (string | number)[][] = [
      ["📋 GUIDE D'UTILISATION DU MODÈLE — Emplois BNSSA & MNS"],
      [""],
      ["⚠️  Limite : 10 annonces maximum par fichier."],
      ["⚠️  Un nouvel import n'est possible qu'après validation de toutes les annonces du lot précédent."],
      [""],
      ["━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━"],
      ["1️⃣  CHAMPS OBLIGATOIRES (marqués d'une *)"],
      ["━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━"],
      ["Champ", "Description", "Valeurs / Format"],
      ["Titre*", "Intitulé du poste", "Texte libre"],
      ["Employeur*", "Nom de l'établissement", "Texte libre"],
      ["Ville*", "Ville du poste", "Texte libre"],
      ["Categorie*", "Univers de l'annonce", VALID_CATEGORIES.join(" | ") + " — liste déroulante"],
      ["TypeContrat*", "Nature du contrat", VALID_CONTRACTS.join(" | ") + " — liste déroulante"],
      [""],
      ["━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━"],
      ["2️⃣  RÈGLES SPÉCIFIQUES PAR TYPE DE CONTRAT"],
      ["━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━"],
      [""],
      ["🔸 Auto-entrepreneur (mission ponctuelle, 1 jour)"],
      ["  • TarifHoraire : OBLIGATOIRE — fourchette (ex: 18 € - 25 €/heure)"],
      ["  • Salaire : laisser vide"],
      ["  • MissionDate : OBLIGATOIRE — AAAA-MM-JJ"],
      ["  • MissionStartTime / MissionEndTime : OBLIGATOIRES — HH:MM"],
      ["  • MissionHours : OBLIGATOIRE — durée totale en heures"],
      ["  • MissionPrice : laisser vide (le candidat AE propose son tarif final)"],
      ["  • Activites : OBLIGATOIRE pour MNS, recommandé pour BNSSA"],
      ["  • ProfilRecherche / Avantages : non utilisés"],
      ["  • Description : optionnelle"],
      [""],
      ["🔸 Intérim (mission ponctuelle, 1 jour)"],
      ["  • Salaire : taux horaire indicatif (ex: 12,50 € - 14 € brut/heure)"],
      ["  • MissionDate : OBLIGATOIRE — AAAA-MM-JJ"],
      ["  • MissionStartTime / MissionEndTime : OBLIGATOIRES — HH:MM"],
      ["  • MissionHours : OBLIGATOIRE — durée totale en heures"],
      ["  • MissionPrice : OBLIGATOIRE — prix TOTAL de la mission en € (ex: 180)"],
      ["  • Activites : OBLIGATOIRE pour MNS, recommandé pour BNSSA"],
      ["  • ProfilRecherche / Avantages : non utilisés"],
      ["  • Description : optionnelle"],
      [""],
      ["🔸 CDI / CDD / temps-partiel-CDI / temps-partiel-CDD"],
      ["  • Salaire : recommandé (ex: 2 200 € - 2 800 € brut/mois)"],
      ["  • Description : OBLIGATOIRE"],
      ["  • ProfilRecherche : recommandé (une exigence par ligne — Alt+Entrée)"],
      ["  • Avantages : recommandé (un avantage par ligne — Alt+Entrée)"],
      ["  • Mission* / Activites : laisser vides"],
      [""],
      ["🔸 Stage / Contractuel / Titulaire"],
      ["  • Salaire : recommandé"],
      ["  • Description : OBLIGATOIRE"],
      ["  • Mission* / Activites : laisser vides"],
      [""],
      ["━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━"],
      ["3️⃣  VALEURS AUTORISÉES (listes déroulantes)"],
      ["━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━"],
      ["Champ", "Valeurs"],
      ["Categorie*", VALID_CATEGORIES.join(" | ")],
      ["TypeContrat*", VALID_CONTRACTS.join(" | ")],
      ["Activites", JOB_ACTIVITIES.join(" | ") + " (séparées par des virgules)"],
      ["Urgent / LogementPossible / HorairesFixes / CoursParticuliers / PossibiliteCDI", "oui | non"],
      ["Deadline / MissionDate", "AAAA-MM-JJ"],
      ["MissionStartTime / MissionEndTime", "HH:MM"],
      ["MissionHours", "Nombre (ex: 8 ou 7.5)"],
      ["MissionPrice", "Nombre en € (ex: 180)"],
      [""],
      ["━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━"],
      ["4️⃣  EXEMPLES CONCRETS"],
      ["━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━"],
      [""],
      ["Exemple 1 — Mission AE BNSSA d'une journée :"],
      ["  Titre : BNSSA — Surveillance événement nautique"],
      ["  Categorie : BNSSA / TypeContrat : auto-entrepreneur"],
      ["  TarifHoraire : 18 € - 25 €/heure"],
      ["  MissionDate : 2025-07-15 / Start : 09:00 / End : 17:00 / Hours : 8"],
      ["  MissionPrice : (vide — le candidat propose)"],
      ["  Activites : Surveillance"],
      [""],
      ["Exemple 2 — Mission Intérim MNS d'une journée :"],
      ["  Titre : MNS — Aquagym événementiel"],
      ["  Categorie : MNS / TypeContrat : interim"],
      ["  Salaire : 14 € brut/heure"],
      ["  MissionDate : 2025-08-03 / Start : 10:00 / End : 16:00 / Hours : 6"],
      ["  MissionPrice : 150"],
      ["  Activites : Aquagym, Enseignement scolaire"],
      [""],
      ["Exemple 3 — Poste CDI MNS classique :"],
      ["  Titre : Maître Nageur Sauveteur — CDI temps plein"],
      ["  Categorie : MNS / TypeContrat : CDI"],
      ["  Salaire : 2 200 € - 2 800 € brut/mois"],
      ["  Description : Surveillance et encadrement des cours..."],
      ["  ProfilRecherche : BPJEPS AAN ou BEESAN | Carte pro valide"],
      ["  Avantages : Mutuelle | 13e mois"],
      [""],
    ];
    const wsHelp = XLSX.utils.aoa_to_sheet(helpAOA);
    wsHelp["!cols"] = [{ wch: 60 }, { wch: 50 }, { wch: 60 }];
    wsHelp["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 2 } },
    ];
    XLSX.utils.book_append_sheet(wb, wsHelp, "Aide");

    // ─── Feuille 3 : Listes (référentiel pour validations) ──────────────────
    const listsAOA: string[][] = [["Categorie", "TypeContrat", "Activites", "OuiNon"]];
    const maxLen = Math.max(
      VALID_CATEGORIES.length,
      VALID_CONTRACTS.length,
      JOB_ACTIVITIES.length,
      2
    );
    for (let i = 0; i < maxLen; i++) {
      listsAOA.push([
        VALID_CATEGORIES[i] || "",
        VALID_CONTRACTS[i] || "",
        JOB_ACTIVITIES[i] || "",
        i === 0 ? "oui" : i === 1 ? "non" : "",
      ]);
    }
    const wsLists = XLSX.utils.aoa_to_sheet(listsAOA);
    wsLists["!cols"] = [{ wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 8 }];
    XLSX.utils.book_append_sheet(wb, wsLists, "Listes");

    // ─── Écriture en buffer + patch DOM pour injecter les <dataValidations> ──
    // Stratégie sûre : on parse sheet1.xml avec DOMParser, on insère le nœud
    // <dataValidations> via createElementNS, puis on re-sérialise avec
    // XMLSerializer. Cela garantit un XML strictement valide, sans risque
    // d'erreur de parsing côté Excel (échappements, ordre, espaces de noms).
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
    const downloadBlob = (blob: Blob, filename: string) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    try {
      const zip = await JSZip.loadAsync(wbout);
      const sheetPath = "xl/worksheets/sheet1.xml";
      const sheetFile = zip.file(sheetPath);
      if (!sheetFile) throw new Error("sheet1.xml introuvable");
      const originalXml = await sheetFile.async("string");

      // Parsing strict du XML existant
      const parser = new DOMParser();
      const doc = parser.parseFromString(originalXml, "application/xml");
      const parserError = doc.getElementsByTagName("parsererror")[0];
      if (parserError) {
        throw new Error("sheet1.xml original déjà invalide : " + parserError.textContent);
      }

      const SS_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
      const worksheet = doc.documentElement;
      if (!worksheet || worksheet.localName !== "worksheet") {
        throw new Error("Élément <worksheet> introuvable");
      }

      // Helpers de plages cibles
      const lastRow = MAX_BULK_JOBS + 1;
      const colLetter = (idx: number): string => {
        let s = "";
        let n = idx;
        do {
          s = String.fromCharCode(65 + (n % 26)) + s;
          n = Math.floor(n / 26) - 1;
        } while (n >= 0);
        return s;
      };
      const rangeFor = (header: (typeof TEMPLATE_HEADERS)[number]) => {
        const c = TEMPLATE_HEADERS.indexOf(header);
        return `${colLetter(c)}2:${colLetter(c)}${lastRow}`;
      };
      // Filtre les valeurs incompatibles avec formula1="a,b,c"
      const sanitizeValues = (vals: string[]) =>
        vals
          .map((v) => v.trim())
          .filter((v) => v.length > 0 && !v.includes(",") && !v.includes('"'));

      type V = { sqref: string; values: string[]; prompt?: string; promptTitle?: string };
      const validations: V[] = [
        {
          sqref: rangeFor("Categorie*"),
          values: sanitizeValues([...VALID_CATEGORIES]),
          promptTitle: "Catégorie",
          prompt: "Choisir BNSSA ou MNS",
        },
        {
          sqref: rangeFor("TypeContrat*"),
          values: sanitizeValues([...VALID_CONTRACTS]),
          promptTitle: "Type de contrat",
          prompt: "AE et Intérim = mission 1 jour (date/horaires/durée requis)",
        },
        {
          sqref: rangeFor("Activites"),
          values: sanitizeValues([...JOB_ACTIVITIES]),
          promptTitle: "Activités",
          prompt: "Choisir une activité dans la liste",
        },
        ...(["Urgent", "LogementPossible", "HorairesFixes", "CoursParticuliers", "PossibiliteCDI"] as const).map(
          (h) => ({ sqref: rangeFor(h), values: ["oui", "non"] } as V)
        ),
      ].filter((v) => v.values.length > 0);

      // Construction du nœud <dataValidations> via createElementNS
      // (DOM gère automatiquement et correctement tous les échappements)
      const dvParent = doc.createElementNS(SS_NS, "dataValidations");
      dvParent.setAttribute("count", String(validations.length));
      for (const v of validations) {
        const dv = doc.createElementNS(SS_NS, "dataValidation");
        dv.setAttribute("type", "list");
        dv.setAttribute("allowBlank", "1");
        dv.setAttribute("showInputMessage", "1");
        dv.setAttribute("showErrorMessage", "1");
        if (v.prompt) {
          dv.setAttribute("promptTitle", v.promptTitle || "");
          dv.setAttribute("prompt", v.prompt);
        }
        dv.setAttribute("sqref", v.sqref);
        const f1 = doc.createElementNS(SS_NS, "formula1");
        // Le contenu de <formula1> doit être de la forme "a,b,c" littérale
        // (avec guillemets droits). DOM se chargera de l'échapper en &quot;.
        f1.textContent = `"${v.values.join(",")}"`;
        dv.appendChild(f1);
        dvParent.appendChild(dv);
      }

      // Insertion au bon endroit selon l'ordre OOXML strict :
      // <dataValidations> doit être après <mergeCells>/<conditionalFormatting>
      // et avant <hyperlinks>/<printOptions>/<pageMargins>/<pageSetup>/<legacyDrawing>.
      const insertBeforeTags = [
        "hyperlinks",
        "printOptions",
        "pageMargins",
        "pageSetup",
        "headerFooter",
        "rowBreaks",
        "colBreaks",
        "customProperties",
        "cellWatches",
        "ignoredErrors",
        "smartTags",
        "drawing",
        "legacyDrawing",
        "legacyDrawingHF",
        "picture",
        "oleObjects",
        "controls",
        "webPublishItems",
        "tableParts",
        "extLst",
      ];

      // Supprime toute <dataValidations> préexistante pour éviter doublon
      const existing = worksheet.getElementsByTagNameNS(SS_NS, "dataValidations");
      for (let i = existing.length - 1; i >= 0; i--) {
        existing[i].parentNode?.removeChild(existing[i]);
      }

      let inserted = false;
      for (const tagName of insertBeforeTags) {
        const matches = worksheet.getElementsByTagNameNS(SS_NS, tagName);
        if (matches.length > 0) {
          const target = matches[0];
          // Ne considérer que les enfants directs de <worksheet>
          if (target.parentNode === worksheet) {
            worksheet.insertBefore(dvParent, target);
            inserted = true;
            break;
          }
        }
      }
      if (!inserted) {
        // Fallback : append à la fin de <worksheet>
        worksheet.appendChild(dvParent);
      }

      // Sérialisation propre + en-tête XML
      const serializer = new XMLSerializer();
      let newXml = serializer.serializeToString(doc);
      // Garantir la déclaration XML en tête (Excel l'exige)
      if (!newXml.startsWith("<?xml")) {
        newXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' + newXml;
      }

      // Validation finale : on re-parse pour vérifier que le résultat est valide
      const verifyDoc = parser.parseFromString(newXml, "application/xml");
      const verifyError = verifyDoc.getElementsByTagName("parsererror")[0];
      if (verifyError) {
        throw new Error("XML produit invalide : " + verifyError.textContent);
      }

      zip.file(sheetPath, newXml);
      const blob = await zip.generateAsync({ type: "blob" });
      downloadBlob(blob, "modele-import-annonces-bnssa-mns.xlsx");
      toast.success("Modèle Excel téléchargé (avec listes déroulantes)");
    } catch (err) {
      console.error("[BulkJobImport] dataValidation patch failed, fallback:", err);
      // Fallback : téléchargement sans validations natives — fichier minimal valide
      XLSX.writeFile(wb, "modele-import-annonces-bnssa-mns.xlsx");
      toast.success("Modèle Excel téléchargé (sans listes déroulantes)");
    }
  }, []);

  // ─── Parsing du fichier importé ────────────────────────────────────────────
  const parseWorkbook = useCallback((wb: XLSX.WorkBook): ParsedJob[] => {
    const sheetName = wb.SheetNames.find((n) => n.toLowerCase() === "annonces") || wb.SheetNames[0];
    const sheet = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<TemplateRow>(sheet, { defval: "" });

    const cleaned = rows.filter((r) => {
      // Garder seulement les lignes avec au moins un titre, employeur ou ville
      return (
        String(r["Titre*"] || "").trim() ||
        String(r["Employeur*"] || "").trim() ||
        String(r["Ville*"] || "").trim()
      );
    });

    return cleaned.slice(0, MAX_BULK_JOBS).map((row, idx) => {
      const errors: string[] = [];
      const get = (k: keyof TemplateRow) => String(row[k] ?? "").trim();
      const getBool = (k: keyof TemplateRow) => {
        const v = get(k).toLowerCase();
        return v === "oui" || v === "yes" || v === "true" || v === "1";
      };
      const getNum = (k: keyof TemplateRow): number | undefined => {
        const raw = String(row[k] ?? "").trim();
        if (!raw) return undefined;
        const n = parseFloat(raw.replace(",", "."));
        return Number.isFinite(n) ? n : undefined;
      };

      const title = get("Titre*");
      const employer = get("Employeur*");
      const location = get("Ville*");
      const department = get("Departement");
      const categoryRaw = get("Categorie*").toUpperCase();
      const contractRaw = get("TypeContrat*");

      if (!title) errors.push("Titre manquant");
      if (!employer) errors.push("Employeur manquant");
      if (!location) errors.push("Ville manquante");
      const category = (VALID_CATEGORIES as string[]).includes(categoryRaw)
        ? (categoryRaw as JobCategory)
        : null;
      if (!category) errors.push(`Categorie invalide (attendu: ${VALID_CATEGORIES.join(" | ")})`);
      const contractType = (VALID_CONTRACTS as string[]).includes(contractRaw)
        ? (contractRaw as ContractType)
        : null;
      if (!contractType)
        errors.push(`TypeContrat invalide (attendu: ${VALID_CONTRACTS.join(" | ")})`);

      const isAEorInterim =
        contractType === "auto-entrepreneur" || contractType === "interim";
      const isInterim = contractType === "interim";

      const description = get("Description");
      if (!isAEorInterim && !description) {
        errors.push("Description requise pour ce type de contrat");
      }

      // Listes multi-lignes
      const splitLines = (raw: string) =>
        raw
          .split(/\r?\n|;/)
          .map((s) => s.trim())
          .filter(Boolean);
      const requirements = isAEorInterim ? [] : splitLines(get("ProfilRecherche"));
      const benefits = isAEorInterim ? [] : splitLines(get("Avantages"));

      // Activités (CSV)
      const activitiesRaw = get("Activites");
      const activities = activitiesRaw
        ? activitiesRaw
            .split(/[,;]/)
            .map((s) => s.trim())
            .filter((s) => (JOB_ACTIVITIES as readonly string[]).includes(s))
        : [];

      // Mission (AE / Intérim)
      const missionDate = get("MissionDate");
      const missionStartTime = get("MissionStartTime");
      const missionEndTime = get("MissionEndTime");
      const missionHours = getNum("MissionHours");
      const missionPrice = getNum("MissionPrice");

      if (isAEorInterim) {
        if (!missionDate) errors.push("MissionDate requise (AE/Intérim)");
        if (!missionStartTime || !missionEndTime)
          errors.push("MissionStartTime et MissionEndTime requises (AE/Intérim)");
        if (typeof missionHours !== "number" || missionHours <= 0)
          errors.push("MissionHours requis (> 0) pour AE/Intérim");
        if (category && category !== "BNSSA" && activities.length === 0) {
          errors.push("Au moins une activité requise (AE/Intérim hors BNSSA)");
        }
      }
      if (isInterim && (typeof missionPrice !== "number" || missionPrice <= 0)) {
        errors.push("MissionPrice requis (> 0) pour Intérim");
      }

      const data: NewRecruiterJob = {
        title,
        employer,
        location,
        department,
        category: category || "BNSSA",
        contractType: contractType || "CDI",
        salary: get("Salaire"),
        hourlyRate: get("TarifHoraire"),
        description,
        requirements,
        benefits,
        deadline: get("Deadline"),
        publishedAt: undefined,
        urgent: getBool("Urgent"),
        logementPossible: getBool("LogementPossible"),
        horairesFixes: getBool("HorairesFixes"),
        coursParticuliers: getBool("CoursParticuliers"),
        possibiliteCDI: getBool("PossibiliteCDI"),
        missionDate: isAEorInterim ? missionDate : "",
        missionStartTime: isAEorInterim ? missionStartTime : "",
        missionEndTime: isAEorInterim ? missionEndTime : "",
        missionHours: isAEorInterim ? missionHours : undefined,
        missionPrice: isInterim ? missionPrice : undefined,
        activities: isAEorInterim ? activities : [],
      };

      return { rowIndex: idx + 2, data, errors }; // +2 car ligne 1 = en-têtes, idx commence à 0
    });
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      try {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const result = parseWorkbook(wb);
        if (result.length === 0) {
          toast.error("Aucune ligne valide trouvée dans le fichier.");
          setParsed(null);
          return;
        }
        if (result.length > MAX_BULK_JOBS) {
          toast.error(`Maximum ${MAX_BULK_JOBS} annonces par fichier.`);
        }
        setParsed(result);
        const errCount = result.filter((r) => r.errors.length > 0).length;
        if (errCount > 0) {
          toast(`${errCount} ligne(s) avec erreurs détectées — merci de corriger.`, {
            icon: "⚠️",
          });
        } else {
          toast.success(`${result.length} annonce(s) prête(s) à importer.`);
        }
      } catch (err) {
        console.error("[BulkJobImport] parse failed:", err);
        toast.error("Fichier illisible. Vérifiez le format Excel (.xlsx).");
      }
    },
    [parseWorkbook]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // reset input pour permettre la ré-importation du même fichier
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ─── Validation et import effectif ─────────────────────────────────────────
  const allValid = useMemo(
    () => parsed !== null && parsed.length > 0 && parsed.every((p) => p.errors.length === 0),
    [parsed]
  );

  const handleImport = useCallback(async () => {
    if (!parsed || !allValid) return;
    if (!recruiterId) {
      toast.error("Session expirée. Veuillez vous reconnecter.");
      return;
    }
    setImporting(true);
    // Génération d'un identifiant de lot lisible
    const ts = new Date()
      .toISOString()
      .replace(/[-:T.Z]/g, "")
      .slice(0, 14);
    const rand = Math.random().toString(36).slice(2, 8);
    const batchId = `${ts}-${rand}`;

    let success = 0;
    let failed = 0;
    for (const item of parsed) {
      try {
        await createJob(
          { ...item.data, bulkBatchId: batchId },
          recruiterId,
          recruiterEmail,
          recruiterName
        );
        success += 1;
      } catch (err) {
        console.error("[BulkJobImport] create failed for row", item.rowIndex, err);
        failed += 1;
      }
    }
    setImporting(false);
    if (success > 0) {
      toast.success(`${success} annonce(s) créée(s) — en attente de validation admin.`);
    }
    if (failed > 0) {
      toast.error(`${failed} annonce(s) n'ont pas pu être créées.`);
    }
    setParsed(null);
    // Re-vérifier l'état du verrou (le batch tout neuf est en pending)
    await fetchLockState();
    onImported?.();
  }, [parsed, allValid, recruiterId, createJob, recruiterEmail, recruiterName, fetchLockState, onImported]);

  // ─── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <FileSpreadsheet className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-gray-900">
            Import en masse via Excel
          </h2>
          <p className="text-xs text-gray-600 mt-0.5">
            Téléchargez le modèle, remplissez jusqu'à <strong>{MAX_BULK_JOBS} annonces</strong> et importez le fichier.
            Toutes les annonces seront créées en attente de validation par l'administration.
          </p>
        </div>
      </div>

      {/* Verrou */}
      {lockLoading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <Loader2 className="w-4 h-4 animate-spin" />
          Vérification de l'état du précédent lot…
        </div>
      ) : lockedBatch ? (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <Lock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-900">
              Nouvel import bloqué : votre dernier lot n'est pas encore entièrement validé.
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Lot <code className="font-mono bg-white/60 px-1 rounded">{lockedBatch.batchId}</code> —
              {" "}
              <strong>{lockedBatch.approved}/{lockedBatch.total}</strong> annonces validées
              {lockedBatch.pending > 0 ? `, ${lockedBatch.pending} en attente` : ""}
              {lockedBatch.rejected > 0 ? `, ${lockedBatch.rejected} refusée(s)` : ""}.
            </p>
            <p className="text-[11px] text-amber-700 mt-1.5 italic">
              Vous pourrez importer un nouveau lot dès que toutes les annonces seront marquées « Publiée » par l'administration.
              Les annonces refusées doivent être corrigées (puis re-soumises individuellement) ou supprimées.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Télécharger le modèle Excel
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 bg-gray-900 hover:bg-black text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              Importer un fichier
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <div className="flex items-start gap-2 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-2.5">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-500" />
            <p>
              Maximum <strong>{MAX_BULK_JOBS} annonces par lot</strong>. Un nouvel import ne sera autorisé qu'une fois
              toutes les annonces du lot précédent validées par l'administration.
            </p>
          </div>

          {/* Aide rapide AE / Intérim / CDI — visible avant téléchargement */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <p className="font-bold text-amber-900 mb-1">🔸 Auto-entrepreneur</p>
              <ul className="text-amber-800 space-y-0.5 list-disc list-inside">
                <li><strong>TarifHoraire</strong> : fourchette (ex: 18 € - 25 €/h)</li>
                <li><strong>MissionDate</strong> + <strong>Start/EndTime</strong> + <strong>Hours</strong> requis</li>
                <li><strong>MissionPrice</strong> : laisser vide</li>
                <li><strong>Activites</strong> : requis (sauf BNSSA)</li>
              </ul>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5">
              <p className="font-bold text-purple-900 mb-1">🔸 Intérim</p>
              <ul className="text-purple-800 space-y-0.5 list-disc list-inside">
                <li><strong>Salaire</strong> : taux horaire indicatif</li>
                <li><strong>MissionDate</strong> + <strong>Start/EndTime</strong> + <strong>Hours</strong> requis</li>
                <li><strong>MissionPrice</strong> : prix TOTAL en € (requis)</li>
                <li><strong>Activites</strong> : requis (sauf BNSSA)</li>
              </ul>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
              <p className="font-bold text-blue-900 mb-1">🔸 CDI / CDD / Stage / etc.</p>
              <ul className="text-blue-800 space-y-0.5 list-disc list-inside">
                <li><strong>Salaire</strong> + <strong>Description</strong> requis</li>
                <li><strong>ProfilRecherche</strong> + <strong>Avantages</strong> recommandés</li>
                <li>Champs <strong>Mission*</strong> : laisser vides</li>
                <li>Listes déroulantes pour Catégorie & Contrat</li>
              </ul>
            </div>
          </div>
        </>
      )}

      {/* Aperçu des lignes parsées */}
      {parsed && parsed.length > 0 && (
        <div className="mt-5 border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-gray-900">
              Aperçu — {parsed.length} annonce{parsed.length > 1 ? "s" : ""}
              {parsed.length > MAX_BULK_JOBS && (
                <span className="ml-2 text-xs font-normal text-amber-700">
                  (les {MAX_BULK_JOBS} premières seulement seront importées)
                </span>
              )}
            </h3>
            <button
              type="button"
              onClick={() => setParsed(null)}
              className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" />
              Annuler
            </button>
          </div>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Ligne</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Titre</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Employeur</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Ville</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Cat.</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Contrat</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {parsed.map((p) => (
                  <tr
                    key={p.rowIndex}
                    className={p.errors.length > 0 ? "bg-red-50" : "hover:bg-gray-50"}
                  >
                    <td className="px-3 py-2 text-gray-600 font-mono">{p.rowIndex}</td>
                    <td className="px-3 py-2 font-medium text-gray-900 max-w-[200px] truncate">
                      {p.data.title || <span className="italic text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-700 max-w-[160px] truncate">
                      {p.data.employer || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{p.data.location || "—"}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded ${
                          p.data.category === "BNSSA"
                            ? "bg-red-100 text-red-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {p.data.category}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {CONTRACT_LABELS[p.data.contractType] || p.data.contractType}
                    </td>
                    <td className="px-3 py-2">
                      {p.errors.length === 0 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          OK
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-red-700 font-semibold"
                          title={p.errors.join(" • ")}
                        >
                          <AlertCircle className="w-3.5 h-3.5" />
                          {p.errors.length} erreur{p.errors.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Détail des erreurs (lignes en rouge) */}
          {parsed.some((p) => p.errors.length > 0) && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
              <p className="font-bold text-red-900 mb-1.5">Erreurs détectées :</p>
              <ul className="space-y-1 text-red-800">
                {parsed
                  .filter((p) => p.errors.length > 0)
                  .map((p) => (
                    <li key={p.rowIndex}>
                      <strong>Ligne {p.rowIndex}</strong> : {p.errors.join(" • ")}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              disabled={!allValid || importing}
              onClick={handleImport}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                allValid && !importing
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Import en cours…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Importer {parsed.filter((p) => p.errors.length === 0).length} annonce
                  {parsed.filter((p) => p.errors.length === 0).length > 1 ? "s" : ""}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
